import { Link as RouterLink } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { cn } from "tailwind-variants";
import { Heading } from "#/_packages/umbrella/components/ui/primitives/heading.tsx";
import { LinkButton } from "#/_packages/umbrella/features/button/components/button.tsx";
import {
	toHoverItemCssClasses,
	toHoverMediaCssClasses,
} from "#/_packages/umbrella/integrations/umbraco/utils/animation-utils.ts";
import type {
	Alignment,
	AnimationScrollType,
	ButtonVariant,
	HeadingLevel,
	Intent,
	Link,
	Orientation,
	ReadWidthLength,
	Variant,
} from "#/_packages/umbrella/types";
import { tryGetHeadingDisplay } from "#/_packages/umbrella/utils";
import { getAltTextFromString } from "#/_packages/umbrella/utils/image-utils.ts";

export type LinkListVariant = "buttons" | "simple" | "submenu" | "teasers";
export type LinkListOrientation =
	| Orientation
	| "orientation-horizontal"
	| "orientation-vertical";

export type LinkListItem = Link & {
	intro?: string;
	isActive?: boolean;
	teaser?: {
		imageSrc?: string;
		imageAlt?: string;
		shareDescription?: string;
	};
};

export type LinkListProps = {
	id?: string;
	links: readonly (LinkListItem | undefined)[];
	type?: LinkListVariant;
	orientation?: LinkListOrientation;
	intentColor?: Intent;
	buttonVariant?: ButtonVariant;
	teaserVariant?: Variant;
	hideIntro?: boolean;
	hideHeading?: boolean;
	headingLevel?: HeadingLevel;
	headingAppearance?: string | null;
	readWidthToggle?: boolean;
	readWidthLength?: ReadWidthLength;
	readWidthAlignment?: Alignment;
	animationScrollType?: AnimationScrollType;
	animationHoverType?: string[];
	applyAnimationToChildren?: boolean;
	staggerAnimation?: boolean;
	currentPath?: string;
	isDrawer?: boolean;
	className?: string;
};

function linkRel(link: LinkListItem) {
	const rel: string[] = [];
	if (link.target === "_blank") rel.push("noopener", "noreferrer");
	if (link.noFollow) rel.push("nofollow");
	return rel.length > 0 ? rel.join(" ") : undefined;
}

function LinkListNavigationLink({
	link,
	className,
	ariaLabel,
	children,
}: {
	link: LinkListItem;
	className?: string;
	ariaLabel?: string;
	children: ReactNode;
}) {
	const rel = linkRel(link);
	if (link.isExternal) {
		return (
			<a
				href={link.href}
				target={link.target}
				data-tracking-event={link.trackingEvent}
				rel={rel}
				className={className}
				aria-label={ariaLabel}
			>
				{children}
			</a>
		);
	}

	return (
		<RouterLink
			to={"/$"}
			params={{ _splat: link.href }}
			target={link.target}
			data-tracking-event={link.trackingEvent}
			rel={rel}
			className={className}
			aria-label={ariaLabel}
		>
			{children}
		</RouterLink>
	);
}

function normalizePath(path: string | undefined) {
	if (!path) return "";
	const normalized = path.length > 1 ? path.replace(/\/+$/, "") : path;
	return normalized || "/";
}

function isActivePath(currentPath: string | undefined, href: string) {
	return normalizePath(currentPath) === normalizePath(href);
}

function toOrientationClass(orientation: string) {
	return orientation === "horizontal" || orientation === "vertical"
		? `orientation-${orientation}`
		: orientation;
}

function toReadWidthLengthClass(length: ReadWidthLength) {
	switch (length) {
		case "narrow":
			return "read-width-narrow";
		case "wide":
			return "read-width-wide";
		default:
			return "read-width-normal";
	}
}

function toTeaserIntentClass(intent: Intent) {
	return intent === "none" ? "teaser-intent-none" : `teaser-intent-${intent}`;
}

function LinkListButtons({
	links,
	intentColor,
	buttonVariant,
	hoverItemClasses,
	childAnimationType,
	staggerClass,
}: {
	links: readonly (LinkListItem | undefined)[];
	intentColor: Intent;
	buttonVariant: ButtonVariant;
	hoverItemClasses?: string | null;
	childAnimationType?: string;
	staggerClass?: string | null;
}) {
	return (
		<div className={cn("link-list link-list-variant-buttons", staggerClass)}>
			{links.map((link) => {
				if (!link) return null;
				return (
					<LinkButton
						key={link.id}
						href={link.href}
						target={link.target}
						data-tracking-event={link.trackingEvent}
						rel={linkRel(link)}
						intent={intentColor}
						variant={buttonVariant}
						className={cn("lg:px-12", hoverItemClasses, childAnimationType)}
					>
						{link.label}
					</LinkButton>
				);
			})}
		</div>
	);
}

function LinkListSimple({
	links,
	hoverItemClasses,
	childAnimationType,
	staggerClass,
}: {
	links: readonly (LinkListItem | undefined)[];
	hoverItemClasses?: string | null;
	childAnimationType?: string;
	staggerClass?: string | null;
}) {
	return (
		<div className={cn("link-list link-list-variant-simple", staggerClass)}>
			{links.map((link) => {
				if (!link) return null;

				return (
					<LinkListNavigationLink
						link={link}
						key={link.id}
						className={cn(
							"link-list-item",
							hoverItemClasses,
							childAnimationType,
						)}
					>
						{link.label}
					</LinkListNavigationLink>
				);
			})}
		</div>
	);
}

function LinkListSubmenu({
	links,
	hideIntro,
	currentPath,
	hoverItemClasses,
	childAnimationType,
	staggerClass,
}: {
	links: readonly (LinkListItem | undefined)[];
	hideIntro: boolean;
	currentPath?: string;
	hoverItemClasses?: string | null;
	childAnimationType?: string;
	staggerClass?: string | null;
}) {
	return (
		<div className="block__submenu">
			<div className="submenu-wrapper">
				<nav className="block__submenu">
					<ul className={cn("submenu-section", staggerClass)}>
						{links.map((link) => {
							if (!link) return null;
							return (
								<li key={link.id} className="nav-item">
									<LinkListNavigationLink
										link={link}
										className={cn(
											"nav-link",
											hoverItemClasses,
											childAnimationType,
											(link.isActive || isActivePath(currentPath, link.href)) &&
												"active",
										)}
									>
										{!hideIntro ? (
											<>
												<span className="font-bold">{link.label}</span>
												{link.intro ? (
													<div className="font-normal">{link.intro}</div>
												) : null}
											</>
										) : (
											<span>{link.label}</span>
										)}
									</LinkListNavigationLink>
								</li>
							);
						})}
					</ul>
				</nav>
			</div>
		</div>
	);
}

function LinkListTeasers({
	links,
	intentColor,
	hideIntro,
	hideHeading,
	headingLevel,
	headingAppearance,
	hoverItemClasses,
	hoverMediaClasses,
	childAnimationType,
	staggerClass,
}: {
	links: readonly (LinkListItem | undefined)[];
	intentColor: Intent;
	hideIntro: boolean;
	hideHeading: boolean;
	headingLevel: HeadingLevel;
	headingAppearance?: string | null;
	hoverItemClasses?: string | null;
	hoverMediaClasses?: string | null;
	childAnimationType?: string;
	staggerClass?: string | null;
}) {
	return (
		<div
			className={cn("content-feed content-feed-variant-teasers", staggerClass)}
		>
			{links.map((link) => {
				if (!link) return null;
				return (
					<div
						key={link?.id}
						className={cn(
							"block__teaser relative flex h-full flex-col overflow-hidden rounded-(--umb-border-radius-lg)",
							toTeaserIntentClass(intentColor),
							hoverItemClasses,
							childAnimationType,
						)}
					>
						{link?.teaser?.imageSrc ? (
							<figure className={cn("figure", hoverMediaClasses)}>
								<img
									src={link.teaser.imageSrc}
									alt={getAltTextFromString(link.teaser.imageAlt ?? link.label)}
									loading="lazy"
									className="w-full object-fit-cover aspect-video"
								/>
							</figure>
						) : null}
						<div className="teaser-content flex flex-col gap-4 p-teaser flex-1 mb-auto">
							{!hideHeading ? (
								<header>
									<Heading
										level={headingLevel}
										display={tryGetHeadingDisplay(headingAppearance) ?? 5}
										className="teaser-title font-teaser-heading! mb-0!"
									>
										{link.label}
									</Heading>
								</header>
							) : null}
							{!hideIntro && link.teaser?.shareDescription ? (
								<div className="teaser-body mb-0-last line-clamp-3 leading-6">
									{link.teaser.shareDescription}
								</div>
							) : null}
						</div>
						<LinkListNavigationLink
							link={link}
							className="stretched-link"
							aria-label={link.label}
						>
							<span className="sr-only">{link.label}</span>
						</LinkListNavigationLink>
					</div>
				);
			})}
		</div>
	);
}

export function LinkList({
	id,
	links,
	type = "simple",
	orientation = "orientation-vertical",
	intentColor = "none",
	buttonVariant = "solid",
	hideIntro = false,
	hideHeading = false,
	headingLevel = 5,
	headingAppearance,
	readWidthToggle = false,
	readWidthLength = "normal",
	animationScrollType = "none",
	animationHoverType = [],
	applyAnimationToChildren = false,
	staggerAnimation = false,
	currentPath,
	className,
}: LinkListProps) {
	if (!links.length) {
		return null;
	}

	const hoverItemClasses = toHoverItemCssClasses(animationHoverType);
	const hoverMediaClasses = toHoverMediaCssClasses(animationHoverType);
	const childAnimationType = applyAnimationToChildren
		? animationScrollType
		: "";
	const rootAnimationType = applyAnimationToChildren ? "" : animationScrollType;
	const staggerClass =
		applyAnimationToChildren && staggerAnimation ? "anim-stagger" : null;

	return (
		<div
			id={id}
			className={cn(
				"block__link-list mb-0-last",
				toOrientationClass(orientation),
				readWidthToggle && "read-width",
				readWidthToggle && toReadWidthLengthClass(readWidthLength),
				rootAnimationType,
				className,
			)}
		>
			{type === "buttons" ? (
				<LinkListButtons
					links={links}
					intentColor={intentColor}
					buttonVariant={buttonVariant}
					hoverItemClasses={hoverItemClasses}
					childAnimationType={childAnimationType}
					staggerClass={staggerClass}
				/>
			) : null}
			{type === "simple" ? (
				<LinkListSimple
					links={links}
					hoverItemClasses={hoverItemClasses}
					childAnimationType={childAnimationType}
					staggerClass={staggerClass}
				/>
			) : null}
			{type === "submenu" ? (
				<LinkListSubmenu
					links={links}
					hideIntro={hideIntro}
					currentPath={currentPath}
					hoverItemClasses={hoverItemClasses}
					childAnimationType={childAnimationType}
					staggerClass={staggerClass}
				/>
			) : null}
			{type === "teasers" ? (
				<LinkListTeasers
					links={links}
					intentColor={intentColor}
					hideIntro={hideIntro}
					hideHeading={hideHeading}
					headingLevel={headingLevel}
					headingAppearance={headingAppearance}
					hoverItemClasses={hoverItemClasses}
					hoverMediaClasses={hoverMediaClasses}
					childAnimationType={childAnimationType}
					staggerClass={staggerClass}
				/>
			) : null}
		</div>
	);
}
